import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  FileText,
  Shield,
  Cookie,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Loader2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PAGE_CONFIG = {
  privacy: {
    title: 'Política de Privacidade',
    icon: Shield,
    color: 'blue'
  },
  terms: {
    title: 'Termos e Condições',
    icon: FileText,
    color: 'emerald'
  },
  cookies: {
    title: 'Política de Cookies',
    icon: Cookie,
    color: 'amber'
  }
};

export default function LegalPageEditor() {
  const { pageType } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const config = PAGE_CONFIG[pageType] || PAGE_CONFIG.privacy;
  const IconComponent = config.icon;

  useEffect(() => {
    fetchContent();
  }, [pageType]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/content/legal/${pageType}`);
      if (response.data.sections && response.data.sections.length > 0) {
        setSections(response.data.sections);
      } else {
        // Load default sections from the static page
        setSections(getDefaultSections(pageType));
      }
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      toast.error('Erro ao carregar conteúdo');
      setSections(getDefaultSections(pageType));
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSections = (type) => {
    // Return empty sections - user will need to add content
    return [
      { id: '1', title: 'Secção 1', content: 'Conteúdo da secção...' }
    ];
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/content/legal/${pageType}`, { sections });
      toast.success('Conteúdo guardado com sucesso');
      setLastUpdated(new Date().toISOString());
    } catch (error) {
      toast.error('Erro ao guardar conteúdo');
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    setSections([
      ...sections,
      { 
        id: Date.now().toString(), 
        title: `Nova Secção ${sections.length + 1}`, 
        content: '' 
      }
    ]);
  };

  const removeSection = (id) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const updateSection = (id, field, value) => {
    setSections(sections.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  };

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < sections.length) {
      [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
      setSections(newSections);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/page-builder')}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-${config.color}-500/10 flex items-center justify-center`}>
              <IconComponent className={`w-5 h-5 text-${config.color}-400`} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Outfit']">
                Editar {config.title}
              </h1>
              {lastUpdated && (
                <p className="text-sm text-slate-400">
                  Última atualização: {new Date(lastUpdated).toLocaleString('pt-PT')}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`/${pageType}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-slate-300 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Pré-visualizar
          </a>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <p className="text-blue-400 text-sm">
          <strong>Nota:</strong> As alterações aqui feitas serão refletidas na página pública. 
          Certifique-se de que o conteúdo está correto antes de guardar.
        </p>
      </div>

      {/* Sections Editor */}
      <div className="space-y-4">
        {sections.map((section, index) => (
          <div 
            key={section.id}
            className="glass-card p-6 border border-white/10"
          >
            <div className="flex items-start gap-4">
              {/* Drag Handle & Move Buttons */}
              <div className="flex flex-col items-center gap-1 pt-2">
                <GripVertical className="w-5 h-5 text-slate-500" />
                <button
                  onClick={() => moveSection(index, 'up')}
                  disabled={index === 0}
                  className="p-1 hover:bg-white/10 rounded disabled:opacity-30"
                >
                  <ChevronUp className="w-4 h-4 text-slate-400" />
                </button>
                <button
                  onClick={() => moveSection(index, 'down')}
                  disabled={index === sections.length - 1}
                  className="p-1 hover:bg-white/10 rounded disabled:opacity-30"
                >
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </div>

              {/* Section Content */}
              <div className="flex-1 space-y-4">
                <div>
                  <Label className="text-slate-300">Título da Secção</Label>
                  <Input
                    value={section.title}
                    onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                    placeholder="Ex: 1. Introdução"
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Conteúdo</Label>
                  <Textarea
                    value={section.content}
                    onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                    placeholder="Escreva o conteúdo desta secção..."
                    rows={6}
                    className="mt-1 bg-[#161C22] border-white/10 text-white rounded-xl resize-y"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Dica: Use listas com "• " no início de cada linha para criar bullet points.
                  </p>
                </div>
              </div>

              {/* Delete Button */}
              <button
                onClick={() => removeSection(section.id)}
                className="p-2 hover:bg-red-500/10 rounded-lg transition-colors group"
                title="Remover secção"
              >
                <Trash2 className="w-5 h-5 text-slate-400 group-hover:text-red-400" />
              </button>
            </div>
          </div>
        ))}

        {/* Add Section Button */}
        <button
          onClick={addSection}
          className="w-full p-4 border-2 border-dashed border-white/20 rounded-xl hover:border-white/40 transition-colors flex items-center justify-center gap-2 text-slate-400 hover:text-white"
        >
          <Plus className="w-5 h-5" />
          Adicionar Nova Secção
        </button>
      </div>

      {/* Quick Links */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Outras Páginas Legais</h3>
        <div className="flex gap-4">
          {Object.entries(PAGE_CONFIG)
            .filter(([key]) => key !== pageType)
            .map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={key}
                  onClick={() => navigate(`/admin/legal-editor/${key}`)}
                  className={`flex items-center gap-2 px-4 py-2 bg-${cfg.color}-500/10 hover:bg-${cfg.color}-500/20 border border-${cfg.color}-500/20 rounded-xl transition-colors`}
                >
                  <Icon className={`w-4 h-4 text-${cfg.color}-400`} />
                  <span className="text-slate-300">{cfg.title}</span>
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
}
